import { logger } from '../../../utils/common/logger'
import { DOMUtils } from '../../../utils/dom'
import './styles/password-requirements.scss' // tslint:disable-line: no-import-side-effect

const PASSWORD_MIN_CHARACTER = 10
const NUMBER_REGEX = /\d/
const UPPER_LOWER_REGEX = /([A-Z].*[a-z]|[a-z].*[A-Z])/
const SPECIAL_CHAR_REGEX = /[$@#%^&*!~`()\-_+={}\\\[\]|:;"'<>,.?/]/
const PASTE_EVENT_TIMEOUT = 100 // time needed to paste text on to the input field

export class PasswordRequirementsList {
    private static DOM = {
        class: {
            passwordRequirementsWrapper: 'password-requirements-wrapper',
            showPasswordRequirementsWrapper: 'password-requirements-wrapper--show',
            validRequirement: 'password-requirement--valid'
        },
        id: {
            passwordRequirementsWrapper: 'password-requirements-wrapper'
        }
    }

    /**
     * Add the requirements list html and event listeners
     */
    public static async initDOM(passwordContainerSelector: string) {
        const passwordContainer = this.getPasswordContainer(passwordContainerSelector)

        if (passwordContainer) {
            const passwordInput = passwordContainer.querySelector('[type=password]')

            try {
                const mod = await import('./templates/password-requirements-list/password-requirements-list.htm')
                const checklistDOM = mod.default
                const wrapper = document.createElement('div')

                DOMUtils.addClass(wrapper, this.DOM.class.passwordRequirementsWrapper)
                // tslint:disable-next-line:no-magic-numbers
                wrapper.id = `id_${Math.random().toString(16).slice(2)}`
                wrapper.innerHTML = checklistDOM
                passwordContainer.after(wrapper)

                passwordInput.setAttribute('aria-describedby', wrapper.id)

                passwordInput.addEventListener('keyup', (event) => {
                    this.validateAndUpdateChecklistUI(passwordContainerSelector, event)
                })

                passwordInput.addEventListener('paste', (event) => {
                    setTimeout(() => {
                        this.validateAndUpdateChecklistUI(passwordContainerSelector, event)
                    }, PASTE_EVENT_TIMEOUT)
                })

                passwordInput.addEventListener('focus', () => {
                    this.showPasswordRequirementsList(passwordContainerSelector)
                })

                logger('info', 'Password requirements list initialized')
            } catch (e) {
                logger('error', 'Password requirements list not initialized', e)
            }
        }
    }

    /**
     * Checks if password meets the minimum length requirement
     *
     * @param password string
     * @returns boolean
     */
    public static validateMinCharacters(password: string): boolean {
        return password.length >= PASSWORD_MIN_CHARACTER
    }

    /**
     * Checks if password has at least 1 number
     *
     * @param password string
     * @returns boolean
     */
    public static validateNumber(password: string): boolean {
        return NUMBER_REGEX.test(password)
    }

    /**
     * Checks if password has at least 1 uppercase letter and 1 lowercase letter
     *
     * @param password string
     * @returns boolean
     */
    public static validateUppercaseAndLowercase(password: string): boolean {
        return UPPER_LOWER_REGEX.test(password)
    }

    /**
     * Checks if password has at least 1 special character
     *
     * @param password string
     * @returns boolean
     */
    public static validateSpecialCharacter(password: string): boolean {
        return SPECIAL_CHAR_REGEX.test(password)
    }

    /**
     * Check if password passes all validation requirements. Returns a results array
     *
     * @returns boolean[]
     */
    public static validateRequirements(passwordInputValue: string): boolean[] {
        const validationResult = []
        validationResult.push(this.validateMinCharacters(passwordInputValue))
        validationResult.push(this.validateNumber(passwordInputValue))
        validationResult.push(this.validateUppercaseAndLowercase(passwordInputValue))
        validationResult.push(this.validateSpecialCharacter(passwordInputValue))
        return validationResult
    }

    /**
     * Update checklist UI
     *
     * @param checklistItems NodeList
     * @param validationResult boolean[]
     */
    public static updateChecklistUI(checklistItems: NodeListOf<HTMLLIElement>, validationResult: boolean[]) {
        checklistItems.forEach((checklistItem: HTMLLIElement, index) => {
            const valid = validationResult[index]

            if (valid) {
                DOMUtils.addClass(checklistItem, this.DOM.class.validRequirement)
            } else {
                DOMUtils.removeClass(checklistItem, this.DOM.class.validRequirement)
            }
        })
    }

    /**
     * Show password requirements list
     */
    public static showPasswordRequirementsList(passwordContainerSelector: string) {
        const element = this.getPasswordRequirementsContainer(passwordContainerSelector)
        if (element && !element.classList.contains(this.DOM.class.showPasswordRequirementsWrapper)) {
            DOMUtils.addClass(element, this.DOM.class.showPasswordRequirementsWrapper)
        }
    }

    /**
     * Validate and update checklist UI
     *
     * @param passwordContainerSelector
     * @param event Event
     *
     */
    private static validateAndUpdateChecklistUI(passwordContainerSelector: string, event: Event) {
        const element = event.target as HTMLInputElement
        const wrapper = this.getPasswordRequirementsContainer(passwordContainerSelector)
        const checklistItems = wrapper.querySelectorAll('li')

        // validate password passwordInput
        const validationResults = this.validateRequirements(element.value)
        this.updateChecklistUI(checklistItems, validationResults)
    }

    private static getPasswordContainer(passwordContainerSelector: string): HTMLElement {
        return document.querySelector(`.${passwordContainerSelector}`)
    }

    private static getPasswordRequirementsContainer(passwordContainerSelector: string): HTMLElement {
        const passwordContainer = this.getPasswordContainer(passwordContainerSelector)
        return passwordContainer && passwordContainer.nextElementSibling as HTMLElement
    }
}
